import type {
    BoardAddPlacement,
    BoardAddTarget,
    BoardColumnDef,
    BoardLaneDef,
    BoardPlacement,
} from "./Board.types";
import type { BoardCellEntry } from "./boardCells";
import { getLaneLabel } from "./boardCollapse";

/** A cell's create buttons, resolved once per render. */
export interface BoardColumnAdding<TItem> {
    placements: ReadonlySet<BoardAddPlacement>;
    /**
     * Gap `+` above the first card and below the last. Each is left out when
     * the header `+` or the footer bar already adds at that end.
     */
    hasGapAboveFirst: boolean;
    hasGapBelowLast: boolean;
    label: string;
    getItemLabel: (item: TItem) => string;
    onAdd: (target: BoardAddTarget) => void;
}

/** A column's create buttons in one lane, or without lanes (`null`). */
export type BoardColumnAddingResolver<TItem> = (
    column: BoardColumnDef,
    lane: BoardLaneDef | null,
) => BoardColumnAdding<TItem> | null;

interface CreateColumnAddingArgs<TItem> {
    column: BoardColumnDef;
    /**
     * Names the lane in the label ("Create task in Prep, Harbor Dental").
     * Null on a board without lanes, the only one showing the header `+`.
     */
    lane?: BoardLaneDef | null;
    getItemLabel: (item: TItem) => string;
    onAddItem?: (target: BoardAddTarget) => void;
    getAddLabel?: (column: BoardColumnDef) => string;
    /** False leaves out the column's `between` buttons. */
    canPlaceBetween: boolean;
}

const getAddPlacements = (
    { addPlacements = [] }: BoardColumnDef,
    canPlaceBetween: boolean,
): ReadonlySet<BoardAddPlacement> =>
    new Set(
        canPlaceBetween
            ? addPlacements
            : addPlacements.filter((placement) => placement !== "between"),
    );

const getGapEdges = (
    placements: ReadonlySet<BoardAddPlacement>,
    hasLanes: boolean,
) => {
    const hasBetween = placements.has("between");
    return {
        hasGapAboveFirst: hasBetween && (hasLanes || !placements.has("header")),
        hasGapBelowLast: hasBetween && !placements.has("footer"),
    };
};

export const createColumnAdding = <TItem>({
    column,
    lane = null,
    getItemLabel,
    onAddItem,
    getAddLabel,
    canPlaceBetween,
}: CreateColumnAddingArgs<TItem>): BoardColumnAdding<TItem> | null => {
    const placements = getAddPlacements(column, canPlaceBetween);
    if (onAddItem === undefined || placements.size === 0) {
        return null;
    }
    return {
        placements,
        ...getGapEdges(placements, lane !== null),
        label: [
            getAddLabel?.(column) ?? `Create item in ${column.title}`,
            ...(lane === null ? [] : [getLaneLabel(lane)]),
        ].join(", "),
        getItemLabel,
        onAdd: onAddItem,
    };
};

interface HasGapAboveFirstArgs {
    columns: readonly BoardColumnDef[];
    hasLanes: boolean;
    onAddItem?: (target: BoardAddTarget) => void;
    canPlaceBetween: boolean;
}

/**
 * Whether any cell shows a gap `+` above its first card. Every cell then
 * makes room for it at the top, so first cards stay level across columns.
 */
export const hasAnyGapAboveFirst = ({
    columns,
    hasLanes,
    onAddItem,
    canPlaceBetween,
}: HasGapAboveFirstArgs) =>
    onAddItem !== undefined &&
    columns.some(
        (column) =>
            getGapEdges(getAddPlacements(column, canPlaceBetween), hasLanes)
                .hasGapAboveFirst,
    );

interface GetBoardAddTargetArgs<TItem> {
    placement: BoardPlacement;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
    /** 0 is the top, `entries.length` the end. */
    index: number;
    button: BoardAddPlacement;
}

/** The target for inserting at `index` among the visible entries. */
export const getBoardAddTarget = <TItem>({
    placement,
    entries,
    index,
    button,
}: GetBoardAddTargetArgs<TItem>): BoardAddTarget => ({
    placement,
    beforeItemId: index > 0 ? entries[index - 1].itemId : null,
    afterItemId: index < entries.length ? entries[index].itemId : null,
    button,
});

interface ShortcutAddTargetArgs<TItem> {
    adding: BoardColumnAdding<TItem>;
    placement: BoardPlacement;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
    /** The focused card's index in the cell. */
    index: number;
    /** Shift+N creates above the card, N below it. */
    side: "above" | "below";
}

/**
 * Where N (below) or Shift+N (above) on a focused card creates one: the gap
 * next to it, like its gap `+`. Without gap buttons (a sorted column) N
 * falls back to the footer bar and Shift+N to the header `+`, which only
 * boards without lanes show. Null when the cell has neither.
 */
export const getShortcutAddTarget = <TItem>({
    adding,
    placement,
    entries,
    index,
    side,
}: ShortcutAddTargetArgs<TItem>): BoardAddTarget | null => {
    const { placements } = adding;
    if (placements.has("between")) {
        return getBoardAddTarget({
            placement,
            entries,
            index: side === "above" ? index : index + 1,
            button: "between",
        });
    }
    if (side === "below" && placements.has("footer")) {
        return getBoardAddTarget({
            placement,
            entries,
            index: entries.length,
            button: "footer",
        });
    }
    if (
        side === "above" &&
        placements.has("header") &&
        placement.laneId === null
    ) {
        return getBoardAddTarget({
            placement,
            entries,
            index: 0,
            button: "header",
        });
    }
    return null;
};

interface GapAddLabelArgs<TItem> {
    adding: BoardColumnAdding<TItem>;
    entries: ReadonlyArray<BoardCellEntry<TItem>>;
    index: number;
}

/** "Create task in Prep above Reconciliation", or "below" for the last gap. */
export const getGapAddLabel = <TItem>({
    adding,
    entries,
    index,
}: GapAddLabelArgs<TItem>) =>
    index < entries.length
        ? `${adding.label} above ${adding.getItemLabel(entries[index].item)}`
        : `${adding.label} below ${adding.getItemLabel(entries[index - 1].item)}`;
